"use client";

import { Spinner } from "@decibeltrade/web-core/components";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { setPendingReferralCode } from "@/hooks/use-pending-referral-code";

interface RedirectContentProps {
  code: string;
  /**
   * Market to land on, already resolved to its `/trade/<segment>` spelling. Undefined lands
   * on `/trade`, which falls back to the visitor's last market.
   */
  market?: string;
}

/**
 * @param code - The referral code to redirect to
 * @param market - The market segment to land on, if the link named one
 * @returns A loading state while the referral code is applied; small delay for UX
 */
export function RedirectContent({ code, market }: RedirectContentProps) {
  const router = useRouter();

  useEffect(() => {
    setPendingReferralCode(code);

    const timer = setTimeout(() => {
      router.push(market ? `/trade/${market}` : "/trade");
    }, 500);

    return () => clearTimeout(timer);
  }, [code, market, router]);

  return (
    <div className={containerStyles}>
      <Spinner size="xl" variant="primary" />
      <span className={messageStyles}>Applying referral code...</span>
    </div>
  );
}

const containerStyles = stack({
  gap: "20",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "screen",
  textAlign: "center",
});

const messageStyles = css({
  textStyle: "heading.md",
  color: "text.secondary",
});
